import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy, doc, deleteDoc, addDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { FaEdit, FaTrash, FaPlus, FaImage, FaUser, FaGraduationCap, FaLinkedin, FaInstagram, FaTwitter, FaGlobe } from 'react-icons/fa';
import './Advisors.scss';
import AdvisorModal from './components/AdvisorModal';

const Advisors = () => {
  const [advisors, setAdvisors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingAdvisor, setEditingAdvisor] = useState(null);

  const advisorsCollectionRef = collection(db, 'myelinAdvisors');

  useEffect(() => {
    fetchAdvisors();
  }, []);

  const fetchAdvisors = async () => {
    try {
      setLoading(true);
      const q = query(advisorsCollectionRef, orderBy('serial', 'asc'));
      const querySnapshot = await getDocs(q);
      
      const advisorsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setAdvisors(advisorsData);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching advisors:', err);
      setError('Failed to fetch advisors. Please try again.');
      setLoading(false);
    }
  };

  const handleAddNewAdvisor = () => {
    setEditingAdvisor(null);
    setShowModal(true);
  };

  const handleEditAdvisor = (advisor) => {
    setEditingAdvisor(advisor);
    setShowModal(true);
  };

  const handleDeleteAdvisor = async (advisorId, advisorName) => {
    const confirmMessage = `Are you sure you want to delete advisor "${advisorName}"? This action cannot be undone.`;
    
    if (window.confirm(confirmMessage)) {
      try {
        await deleteDoc(doc(db, 'myelinAdvisors', advisorId));
        setAdvisors(advisors.filter(advisor => advisor.id !== advisorId));
        
        // Show success message
        setError(null);
        alert(`Advisor "${advisorName}" has been successfully deleted.`);
      } catch (err) {
        console.error('Error deleting advisor:', err);
        setError('Failed to delete advisor. Please try again.');
      }
    }
  };

  const handleSaveAdvisor = async (advisorData) => {
    try {
      if (editingAdvisor) {
        // Update existing advisor
        const advisorRef = doc(db, 'myelinAdvisors', editingAdvisor.id);
        await updateDoc(advisorRef, {
          ...advisorData,
          updatedAt: serverTimestamp()
        });
        
        // Update local state
        setAdvisors(advisors.map(advisor => 
          advisor.id === editingAdvisor.id 
            ? { ...advisor, ...advisorData }
            : advisor
        ));
        
        alert('Advisor updated successfully!');
      } else {
        // Create new advisor
        const newAdvisorRef = await addDoc(advisorsCollectionRef, {
          ...advisorData,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        
        // Add to local state
        setAdvisors([...advisors, { id: newAdvisorRef.id, ...advisorData }]);
        
        alert('Advisor added successfully!');
      }
      
      setShowModal(false);
      setEditingAdvisor(null);
      setError(null);
    } catch (err) {
      console.error('Error saving advisor:', err);
      setError('Failed to save advisor. Please try again.');
    }
  };

  const renderAdvisorCard = (advisor) => {
    return (
      <div className="advisor-card" key={advisor.id}>
        <div className="advisor-card-content">
          <div className="advisor-image">
            {advisor.img ? (
              <img src={advisor.img} alt={advisor.name} />
            ) : (
              <div className="no-image">
                <FaUser style={{ fontSize: '2rem', opacity: 0.5, marginBottom: '8px' }} />
                <span>No Image</span>
              </div>
            )}
            <div className="advisor-serial">#{advisor.serial || 'N/A'}</div>
          </div>
          
          <div className="advisor-details">
            <div className="advisor-actions">
              <button 
                className="edit-btn" 
                onClick={() => handleEditAdvisor(advisor)}
                aria-label="Edit advisor"
              >
                <FaEdit /> Edit
              </button>
              <button 
                className="delete-btn" 
                onClick={() => handleDeleteAdvisor(advisor.id, advisor.name)}
                aria-label="Delete advisor"
              >
                <FaTrash /> Delete
              </button>
            </div>
            
            <h3>{advisor.name}</h3>
            
            <div className="advisor-designation">
              <FaGraduationCap className="icon" />
              <span>{advisor.designation}</span>
            </div>
            
            <div className="advisor-organogram">
              <span className="organogram-badge">{advisor.organogram}</span>
            </div>
            
            {advisor.shortDescription && (
              <div className="advisor-short-description">
                <p>{advisor.shortDescription.substring(0, 150)}{advisor.shortDescription.length > 150 ? '...' : ''}</p>
              </div>
            )}
            
            <div className="advisor-social">
              {advisor.socialLinkedIn && (
                <a href={advisor.socialLinkedIn} target="_blank" rel="noopener noreferrer" className="social-link linkedin">
                  <FaLinkedin />
                </a>
              )}
              {advisor.socialTwitter && (
                <a href={advisor.socialTwitter} target="_blank" rel="noopener noreferrer" className="social-link twitter">
                  <FaTwitter />
                </a>
              )}
              {advisor.socialInstagram && (
                <a href={advisor.socialInstagram} target="_blank" rel="noopener noreferrer" className="social-link instagram">
                  <FaInstagram />
                </a>
              )}
              {advisor.socialWeb && (
                <a href={advisor.socialWeb} target="_blank" rel="noopener noreferrer" className="social-link website">
                  <FaGlobe />
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="advisors-management-container">
      <div className="page-header">
        <h1>Advisors Management</h1>
        <p>Manage MyelinZ advisors and their information.</p>
        <button 
          className="add-advisor-btn" 
          onClick={handleAddNewAdvisor}
        >
          <FaPlus /> Add New Advisor
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}
      
      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading advisors...</p>
        </div>
      ) : advisors.length === 0 ? (
        <div className="no-advisors">
          <p>No advisors found. Click "Add New Advisor" to create the first advisor profile.</p>
        </div>
      ) : (
        <div className="advisors-grid">
          {advisors.map(advisor => renderAdvisorCard(advisor))}
        </div>
      )}

      {showModal && (
        <AdvisorModal 
          advisor={editingAdvisor}
          onClose={() => {
            setShowModal(false);
            setEditingAdvisor(null);
          }} 
          onSave={handleSaveAdvisor}
        />
      )}
    </div>
  );
};

export default Advisors; 